/** Shared book-meeting / Calendly domain constants. */

export const BookMeetingCallType = {
  SUPPORT: "support",
  RISK: "risk",
  SALES: "sales",
  OPERATIONS: "operations",
  RM: "rm",
} as const;

export type BookMeetingCallTypeValue =
  (typeof BookMeetingCallType)[keyof typeof BookMeetingCallType];

export const BookMeetingTabParam = {
  RISK_TEAM: "risk-team",
  OPERATIONS_TEAM: "operations-team",
} as const;

export type BookMeetingTabParamValue =
  (typeof BookMeetingTabParam)[keyof typeof BookMeetingTabParam];

export const BookMeetingQueryKey = {
  TAB: "tab",
  ACCOUNT_ID: "accountId",
} as const;

/** CRM Users menu deep-link query key for book-meeting contact notifications. */
export const UsersTableHighlightQueryKey = {
  CONTACT_ID: "highlightContactId",
} as const;

/** Platform account ids are UUIDs. Placeholders like "unknown" / "Incomlend" must not notify. */
export const BOOK_MEETING_ACCOUNT_ID_PATTERN =
  /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$/;

/** URL tab → call-type value mapping. */
export const BOOK_MEETING_TAB_TO_CALL_TYPE: Record<
  BookMeetingTabParamValue,
  BookMeetingCallTypeValue
> = {
  [BookMeetingTabParam.RISK_TEAM]: BookMeetingCallType.RISK,
  [BookMeetingTabParam.OPERATIONS_TEAM]: BookMeetingCallType.OPERATIONS,
};

/** Call types that use company name as Calendly invitee name (invite subject). */
export const COMPANY_SUBJECT_CALL_TYPES = new Set<string>([
  BookMeetingCallType.RISK,
  BookMeetingCallType.SALES,
  BookMeetingCallType.OPERATIONS,
  BookMeetingCallType.SUPPORT,
  BookMeetingCallType.RM,
]);

export const RM_FALLBACK_EMAIL = "rm@incomlend.com";

export const RM_MEETING_DESCRIPTION =
  "Direct 1:1 meeting with your assigned Relationship Manager";

/** Dropdown/tab labels (may differ from Calendly card titles in MANAGERS). */
export const BookMeetingCallTypeLabel = {
  [BookMeetingCallType.SUPPORT]: "Support Team",
  [BookMeetingCallType.RISK]: "Risk Team",
  [BookMeetingCallType.SALES]: "Sales Team",
  [BookMeetingCallType.OPERATIONS]: "Operations Team",
} as const;

export const BOOK_MEETING_MANAGERS = {
  [BookMeetingCallType.SUPPORT]: {
    title: "Support Team",
    email: "support@incomlend.com",
    calendlyUrl: "https://calendly.com/incomlend/support-test",
    description:
      "Get help with platform usage, technical issues, and general inquiries",
  },
  [BookMeetingCallType.RISK]: {
    title: "Incomlend Review Team",
    email: "risk@incomlend.com",
    calendlyUrl: "https://calendly.com/incomlend/meeting-with-risk-team",
    description:
      "Discuss risk assessment, credit evaluations, and compliance matters",
  },
  [BookMeetingCallType.SALES]: {
    title: "Sales Team",
    email: "sales@incomlend.com",
    calendlyUrl: "https://calendly.com/incomlend/meeting-with-sales-team",
    description:
      "Explore financing options, business development, and partnership opportunities",
  },
  [BookMeetingCallType.OPERATIONS]: {
    title: "Operations Team",
    email: "operations@incomlend.com",
    calendlyUrl: "https://calendly.com/incomlend/meeting-with-operation-team",
    description:
      "Assistance with operational processes, documentation, and workflow management",
  },
} as const;

export type BookMeetingManagerKey = keyof typeof BOOK_MEETING_MANAGERS;

export const BASE_CALENDLY_CONFIG = {
  utm: {
    utmCampaign: "incomlend_booking",
    utmSource: "incomlend_platform",
    utmMedium: "web",
  },
} as const;
